import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import MemberRoleSelect from '../../../../components/MemberRoleSelect';
import { createOccupant } from '../../../../redux/actions/SocietyPortal/OccupantAction/OccupantAction';
import { getWingsDropdown, getFlatsDropdown } from '../../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import usePermissions from '../../../../hook/usePermissions';
import { PERMISSIONS } from '../../../../consts/permissions';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_MEMBERS_FAMILY } from '../../../../Routing/routes';

const RELATION_OPTIONS = [
    { value: 'SPOUSE', label: 'Spouse' },
    { value: 'CHILD', label: 'Child' },
    { value: 'PARENT', label: 'Parent' },
    { value: 'SIBLING', label: 'Sibling' },
    { value: 'OTHER', label: 'Other' },
];

const AddFamily = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { createLoading } = useSelector(state => state.OccupantReducer);
    const { wingsDropdown, wingsDropdownLoading, flatsDropdown, flatsDropdownLoading } = useSelector(state => state.WingsAndFlatsReducers);
    const { can } = usePermissions();
    // Anyone who may add / edit members picks the login role; the options
    // (and the API) limit non-admins to member roles.
    const canPickRole = can(PERMISSIONS.USER_MANAGE_ROLES) || can(PERMISSIONS.OCCUPANT_CREATE);

    const wings = wingsDropdown || [];
    const flats = flatsDropdown || [];

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        relation: 'all',
        wing_id: 'all',
        flat_id: 'all',
        start_date: '',
        role_id: '',
        // Whether the family member actually lives in the flat. occupants
        // .is_resident has always existed and the list, cards and owner view all
        // render it, but there was no control to set it — so every family member
        // was created with the column's 0 default and showed as "Non-Residing".
        is_resident: true,
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        const societyId = selectedSocietyStorer.getSocietyId();
        if (societyId) {
            dispatch(getWingsDropdown({ society_id: societyId }));
        }
    }, [dispatch]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleRelationChange = (value) => {
        setFormData(prev => ({ ...prev, relation: value }));
        if (errors.relation) {
            setErrors(prev => ({ ...prev, relation: '' }));
        }
    };

    const handleWingChange = (value) => {
        setFormData(prev => ({ ...prev, wing_id: value, flat_id: 'all' }));
        if (errors.wing_id) setErrors(prev => ({ ...prev, wing_id: '' }));
        if (value !== 'all') {
            dispatch(getFlatsDropdown({ wing_id: value }));
        }
    };

    const handleFlatChange = (value) => {
        setFormData(prev => ({ ...prev, flat_id: value }));
        if (errors.flat_id) setErrors(prev => ({ ...prev, flat_id: '' }));
    };

    const handleRoleChange = (value) => {
        setFormData(prev => ({ ...prev, role_id: value }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.first_name.trim()) newErrors.first_name = 'First Name is required';
        if (!formData.last_name.trim()) newErrors.last_name = 'Last Name is required';
        // Phone is optional for family members — most children/dependents
        // don't have their own number, and family rows no longer get a login
        // provisioned so there's no downstream login-email dependency on
        // phone. If provided, still enforce the 10-digit format.
        if (formData.phone && !/^\d{10}$/.test(formData.phone.trim())) {
            newErrors.phone = 'Phone must be exactly 10 digits';
        }
        if (formData.relation === 'all') newErrors.relation = 'Relation is required';
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }
        if (formData.wing_id === 'all') newErrors.wing_id = 'Wing is required';
        if (formData.flat_id === 'all') newErrors.flat_id = 'Flat is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const societyId = selectedSocietyStorer.getSocietyId();
        const payload = {
            society_id: societyId,
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email || undefined,
            phone: formData.phone || undefined,
            relation: formData.relation,
            type: 'FAMILY',
            wing_id: formData.wing_id,
            flat_id: formData.flat_id,
            // occupantValidator declares is_resident as Joi.boolean(), which
            // rejects 1/0 outright — send a real boolean, as EditTenantModal does.
            is_resident: !!formData.is_resident,
        };
        if (formData.start_date) payload.start_date = formData.start_date;
        if (canPickRole && formData.role_id) payload.role_id = Number(formData.role_id);

        payload.onSuccess = () => {
            navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MEMBERS_FAMILY);
        };

        dispatch(createOccupant(payload));
    };

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <button
                        onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MEMBERS_FAMILY)}
                        className="tw-p-2 tw-rounded-lg tw-bg-gray-100 hover:tw-bg-gray-200 tw-text-gray-600"
                    >
                        <Icon icon="mdi:arrow-left" className="tw-text-lg" />
                    </button>
                    <div>
                        <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Add Family Member</h1>
                        <p className="tw-text-xs tw-text-gray-500">Fill in the family member details below</p>
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-3">
                    <button
                        type="button"
                        onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MEMBERS_FAMILY)}
                        className="tw-px-6 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="addFamilyForm"
                        disabled={createLoading}
                        className="tw-px-5 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-rounded-lg tw-flex tw-items-center tw-gap-1.5 disabled:tw-opacity-50 tw-transition-colors"
                    >
                        {createLoading ? (
                            <>
                                <Icon icon="svg-spinners:ring-resize" className="tw-text-base" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Icon icon="mingcute:save-line" className="tw-text-base" />
                                Save
                            </>
                        )}
                    </button>
                </div>
            </div>

            <form id="addFamilyForm" onSubmit={handleSubmit}>
                {/* Personal Information */}
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4 tw-mb-4">
                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                        <Icon icon="mdi:account-outline" className="tw-text-blue-500" />
                        Personal Information
                    </h2>
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4">
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                First Name <span className="tw-text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleChange}
                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.first_name ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                placeholder="Enter first name"
                                maxLength={50}
                            />
                            {errors.first_name && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.first_name}</p>}
                        </div>
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                Last Name <span className="tw-text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleChange}
                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.last_name ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                placeholder="Enter last name"
                                maxLength={50}
                            />
                            {errors.last_name && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.last_name}</p>}
                        </div>
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                Phone <span className="tw-text-gray-400 tw-font-normal">(Optional)</span>
                            </label>
                            <input
                                type="text"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.phone ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                placeholder="Phone Number"
                                maxLength={15}
                            />
                            {errors.phone && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.phone}</p>}
                        </div>
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Residing in Flat</label>
                            <label className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-cursor-pointer">
                                <input
                                    type="checkbox"
                                    name="is_resident"
                                    checked={formData.is_resident}
                                    onChange={(e) => setFormData(prev => ({ ...prev, is_resident: e.target.checked }))}
                                />
                                <span className={`tw-text-sm tw-font-medium ${formData.is_resident ? 'tw-text-emerald-600' : 'tw-text-gray-500'}`}>
                                    {formData.is_resident ? 'Residing' : 'Non-Residing'}
                                </span>
                            </label>
                        </div>
                        {canPickRole && (
                            <div>
                                <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Login Role</label>
                                <MemberRoleSelect
                                    value={formData.role_id}
                                    onChange={handleRoleChange}
                                    placeholder="Default: Resident"
                                    className="tw-w-full"
                                />
                                <p className="tw-text-[10px] tw-text-gray-400 tw-mt-0.5">Leave blank to default to Resident</p>
                            </div>
                        )}
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Email</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.email ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                placeholder="Email Address"
                                maxLength={100}
                            />
                            {errors.email && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.email}</p>}
                        </div>
                    </div>
                </div>

                {/* Unit Details */}
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4 tw-mb-4">
                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                        <Icon icon="mdi:home-outline" className="tw-text-blue-500" />
                        Unit Details
                    </h2>
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4">
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                Wing <span className="tw-text-red-500">*</span>
                            </label>
                            <SearchableDropdown
                                value={formData.wing_id}
                                onChange={handleWingChange}
                                options={wings}
                                valueKey="wing_id"
                                labelKey="title"
                                placeholder="Select Wing"
                                loading={wingsDropdownLoading}
                                className="tw-w-full"
                            />
                            {errors.wing_id && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.wing_id}</p>}
                        </div>
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                Flat <span className="tw-text-red-500">*</span>
                            </label>
                            <SearchableDropdown
                                value={formData.flat_id}
                                onChange={handleFlatChange}
                                options={flats}
                                valueKey="flat_id"
                                labelKey="flat_number"
                                placeholder="Select Flat"
                                loading={flatsDropdownLoading}
                                className="tw-w-full"
                            />
                            {errors.flat_id && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.flat_id}</p>}
                        </div>
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                Start Date <span className="tw-text-gray-400 tw-font-normal">(optional - defaults to today)</span>
                            </label>
                            <input
                                type="date"
                                name="start_date"
                                value={formData.start_date}
                                onChange={handleChange}
                                className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
                            />
                        </div>
                    </div>
                </div>

                {/* Relationship */}
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4 tw-mb-4">
                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                        <Icon icon="mdi:heart-outline" className="tw-text-blue-500" />
                        Relationship
                    </h2>
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4">
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                Relation <span className="tw-text-red-500">*</span>
                            </label>
                            <SearchableDropdown
                                value={formData.relation}
                                onChange={handleRelationChange}
                                options={RELATION_OPTIONS}
                                valueKey="value"
                                labelKey="label"
                                placeholder="Select Relation"
                                className="tw-w-full"
                            />
                            {errors.relation && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.relation}</p>}
                        </div>
                        {/* Login Role picker removed — family members no
                            longer get an auto-provisioned login (their
                            phone/email would collide with the owner's), so
                            picking a role has no effect. */}
                    </div>
                </div>

            </form>
        </>
    );
};

export default AddFamily;
